/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable function-paren-newline */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton } from '@folio/stripes-components';
import { Tooltip } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { GrDuplicate } from 'react-icons/gr';

import { get } from 'lodash';
import css from './styles.css';
import useBarcodeGenerator from '../../../../../hooks/Barcode/useBarcodeGenerator';
import ConfirmDeleteItemModal from './ItemsList/modals/ConfirmDeleteItemModal';
import DuplicateItemsModal from './ItemsList/modals/DuplicateItemsModal';
import DuplicateSingleItemModal from './ItemsList/modals/DuplicateSingleItemModal';
import { useFormPageErrorsContext } from '../../../../../providers/FormPagesErrorsProvider';
import { useBarcodeSession } from '../../../../../contexts/BarcodeSessionContext';
import { useField } from 'react-final-form';

function ItemAccordionHeader({
  fields,
  index,
  permanentLocationId,
  locationCode,
  controlledVocab,
  isDisabledItemActions,
  template,
  onEditItem,
  createUndoAction,
}) {
  const { updateError } = useFormPageErrorsContext();
  const { removeBarcodes, addBarcodes } = useBarcodeSession();
  const { generateBarcodeValue } = useBarcodeGenerator();
  const [openDeleteItemModal, setOpenDeleteItemModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showSingleDuplicateModal, setShowSingleDuplicateModal] =
    useState(false);

  const instanceStatisticalCodeIdsField = useField('instanceData.statisticalCodeIds');
  const instanceStatisticalCodeIds = instanceStatisticalCodeIdsField?.input?.value || [];

  const templatesConfigs = controlledVocab.templatesConfigs;

  const {
    configValue: { enableGenerator, autoGenerate },
  } = templatesConfigs.find(
    (config) => config.configName === 'generatorSettings',
  );

  const setBarcodeValue = (newIndex) => {
    if (enableGenerator && autoGenerate) {
      return generateBarcodeValue(permanentLocationId, locationCode, newIndex);
    } else {
      return '';
    }
  };

  // Handle duplicate items from new modal
  const handleDuplicateItems = (itemCount, barcodeSettings) => {
    const validItemCount = Math.max(1, parseInt(itemCount, 10) || 1);

    for (let i = 0; i < validItemCount; i++) {
      const newIndex = i + 1;
      const itemId = uuidv4();

      // Use sequential barcode if available, otherwise auto-generate
      let barcode = '';
      if (
        barcodeSettings &&
        barcodeSettings.barcodes &&
        barcodeSettings.barcodes.length > i
      ) {
        barcode = barcodeSettings.barcodes[i] || '';
      } else {
        barcode = setBarcodeValue(newIndex);
      }

      fields.push({
        ...fields.value[index],
        id: itemId,
        itemHrid: '',
        barcode,
        copyNumber: fields.value ? fields.value.length + newIndex : 1,
        ...(instanceStatisticalCodeIds.length && { itemStatisticalCodeIds: instanceStatisticalCodeIds }),
      });
    }
  };

  // Handle single item duplication from edit modal
  const handleSingleItemDuplicate = (editedValues) => {
    const itemId = uuidv4();

    const newItem = {
      ...editedValues,
      id: itemId,
      itemHrid: '',
      copyNumber: fields.value ? fields.value.length + 1 : 1,
    };

    fields.push(newItem);

    // Add barcode to session tracking if it exists
    if (newItem.barcode) {
      addBarcodes([newItem.barcode]);
    }
  };

  return (
    <>
      <div style={{ zIndex: 9999 }}>
        <ConfirmDeleteItemModal
          open={openDeleteItemModal}
          canDelete={get(fields.value[index], 'status.name') === 'Available'}
          onClose={() => setOpenDeleteItemModal(false)}
          onRemove={() => {
            // Get item barcode before deletion for session cleanup
            const itemToDelete = fields.value[index];
            const itemBarcode = itemToDelete?.barcode;

            // Create undo action for single item deletion
            if (createUndoAction) {
              createUndoAction(
                'DELETE_ITEMS',
                {
                  deletedItems: [{ item: { ...itemToDelete }, index }],
                  deletedBarcodes: itemBarcode ? [itemBarcode] : [],
                },
                'undoSystem.actions.deleteItem',
              );
            }

            // Remove item from fields
            fields.remove(index);
            setOpenDeleteItemModal(false);
            updateError(itemToDelete.id, false);

            // Remove barcode from session tracking if it exists
            if (itemBarcode) {
              removeBarcodes([itemBarcode]);
            }
          }}
          itemRecord={fields.value[index]}
        />
      </div>
      <Tooltip
        id="item-accordion-header-duplicate-item-multiple-tooltip"
        text={
          <FormattedMessage
            id="ui-plugin-inventory-templates.itemAccordionHeader.duplicateMultipleBtn.tooltip.msg"
            defaultMessage="Duplicate multiple"
          />
        }
      >
        {({ ref, ariaIds }) => (
          <Button
            buttonStyle="slim"
            onClick={() => setShowDuplicateModal(true)}
            aria-labelledby={ariaIds.text}
            ref={ref}
            disabled={isDisabledItemActions}
            marginBottom0
          >
            <GrDuplicate size={15} />
          </Button>
        )}
      </Tooltip>
      <Tooltip
        id="item-accordion-header-duplicate-item-tooltip"
        text={
          <FormattedMessage
            id="ui-plugin-inventory-templates.tooltips.duplicate.msg"
            defaultMessage="Duplicate"
          />
        }
      >
        {({ ref, ariaIds }) => (
          <IconButton
            icon="duplicate"
            onClick={() => setShowSingleDuplicateModal(true)}
            aria-labelledby={ariaIds.text}
            ref={ref}
            disabled={isDisabledItemActions}
          />
        )}
      </Tooltip>

      {/* Edit Item Button */}
      <Tooltip
        id="item-accordion-header-edit-item-tooltip"
        text={
          <FormattedMessage
            id="ui-plugin-inventory-templates.tooltips.edit.msg"
            defaultMessage="Edit"
          />
        }
      >
        {({ ref, ariaIds }) => (
          <IconButton
            icon="edit"
            onClick={() => onEditItem && onEditItem(index)}
            aria-labelledby={ariaIds.text}
            ref={ref}
            disabled={isDisabledItemActions}
          />
        )}
      </Tooltip>

      <IconButton
        icon="trash"
        onClick={() => {
          setOpenDeleteItemModal(true);
        }}
        onMouseDown={(e) => e.preventDefault()}
        disabled={isDisabledItemActions}
      />

      {/* New Duplicate Items Modal */}
      <DuplicateItemsModal
        open={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        onDuplicateItems={handleDuplicateItems}
        originalItem={fields.value[index]}
        holdingLocationId={permanentLocationId}
        template={null}
        classificationNumber=""
        authorNumber=""
        currentItemsCount={fields.value ? fields.value.length : 0}
      />

      {/* Single Item Duplicate Modal */}
      <DuplicateSingleItemModal
        open={showSingleDuplicateModal}
        onClose={() => setShowSingleDuplicateModal(false)}
        onConfirm={handleSingleItemDuplicate}
        originalItem={fields.value[index]}
        template={template}
        controlledVocab={controlledVocab}
        currentItemsCount={fields.value ? fields.value.length : 0}
      />
    </>
  );
}

ItemAccordionHeader.propTypes = {
  fields: PropTypes.object,
  index: PropTypes.number,
  permanentLocationId: PropTypes.string,
  locationCode: PropTypes.string,
  controlledVocab: PropTypes.object,
  isDisabledItemActions: PropTypes.bool,
  template: PropTypes.object,
  onEditItem: PropTypes.func,
  createUndoAction: PropTypes.func,
};

export default ItemAccordionHeader;
